import { useEffect, useRef, useState } from 'react';
import './AvatarCropper.css';

/**
 * Circular avatar cropper. Takes the file the member picked, lets them pan
 * (drag) and zoom (slider) inside a round viewport, and returns a square
 * JPEG covering that viewport - the Avatar component's round mask does the
 * final circle, so we export a square and avoid JPEG-has-no-alpha corners.
 */

interface Props {
  file: File;
  onCancel: () => void;
  onConfirm: (cropped: File) => void;
}

const VIEWPORT = 288; // on-screen crop square (px)
const OUTPUT = 512; // exported square (px)

export function AvatarCropper({ file, onCancel, onConfirm }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  // Object URL for the picked file, revoked on unmount.
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  // baseScale = cover fit so the image always fills the viewport at scale 1.
  const baseScale = natural ? Math.max(VIEWPORT / natural.w, VIEWPORT / natural.h) : 1;
  const dispW = natural ? natural.w * baseScale * scale : 0;
  const dispH = natural ? natural.h * baseScale * scale : 0;

  // Keep the image covering the viewport - clamp pan to the overflow.
  function clamp(o: { x: number; y: number }) {
    const maxX = Math.max(0, (dispW - VIEWPORT) / 2);
    const maxY = Math.max(0, (dispH - VIEWPORT) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, o.x)),
      y: Math.min(maxY, Math.max(-maxY, o.y)),
    };
  }

  useEffect(() => {
    setOffset((o) => clamp(o));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, natural]);

  function onPointerDown(e: React.PointerEvent) {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    setOffset(clamp({ x: drag.current.ox + dx, y: drag.current.oy + dy }));
  }
  function onPointerUp() {
    drag.current = null;
  }

  function confirm() {
    const img = imgRef.current;
    if (!img || !natural) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    const k = OUTPUT / VIEWPORT; // viewport → output scale
    const dw = dispW * k;
    const dh = dispH * k;
    const dx = OUTPUT / 2 - dw / 2 + offset.x * k;
    const dy = OUTPUT / 2 - dh / 2 + offset.y * k;
    ctx.drawImage(img, dx, dy, dw, dh);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onConfirm(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  }

  return (
    <div className="cropper" role="dialog" aria-modal="true" aria-label="Crop your photo">
      <div className="cropper__panel">
        <p className="cropper__title">Position your photo</p>
        <div
          className="cropper__stage"
          style={{ width: VIEWPORT, height: VIEWPORT }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {url && (
            <img
              ref={imgRef}
              src={url}
              alt=""
              draggable={false}
              onLoad={(e) => {
                const el = e.currentTarget;
                setNatural({ w: el.naturalWidth, h: el.naturalHeight });
              }}
              style={{
                width: dispW || undefined,
                height: dispH || undefined,
                transform: `translate(${(VIEWPORT - dispW) / 2 + offset.x}px, ${(VIEWPORT - dispH) / 2 + offset.y}px)`,
              }}
            />
          )}
          <span className="cropper__ring" aria-hidden="true" />
        </div>

        <label className="cropper__zoom">
          <span aria-hidden="true">−</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            aria-label="Zoom"
          />
          <span aria-hidden="true">+</span>
        </label>

        <div className="cropper__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary btn--sm" onClick={confirm} disabled={!natural}>
            Use photo
          </button>
        </div>
      </div>
    </div>
  );
}
